import {
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import { HourlyAccountConfigFeatureEnum, HourlyCategory } from 'parkchirp-shared/enums';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CommonContext } from '../../../contexts';
import { useDebounce } from '../../../hooks';
import { SET_HOURLY_ACCOUNT_CONFIG } from '../../../mutations';
import { GET_AVAILABLE_ZONES } from '../../../queries';
import type { HourlyAccountConfig } from '../../../types';
import { dayjs } from '../../../utils';

export interface StartSessionConfigEditorProps {
	readonlyConfigs?: HourlyAccountConfig[];
	facilityId?: string;
	refetchHourlyAccount?: () => void;
	isPageEditing?: boolean;
	isParentLoading?: boolean;
	accountId?: string;
	isAccountConfigEditing?: boolean;
	setAccountConfigEditing?: (editing: boolean) => void;
}

export const StartSessionConfigEditor = ({
	readonlyConfigs = [],
	facilityId,
	refetchHourlyAccount,
	isPageEditing,
	isParentLoading,
	accountId,
	setAccountConfigEditing,
	isAccountConfigEditing,
}) => {
	const { setShowSnackbar, apolloHeaders } = useContext(CommonContext);
	const startSessionConfig = useMemo(() => {
		const config = readonlyConfigs.find(
			(config) => config.feature === HourlyAccountConfigFeatureEnum.START_SESSION,
		);

		if (config) {
			return JSON.parse(config.config || '{}');
		}

		return {};
	}, [readonlyConfigs]);

	const [setHourlyAccountConfigMutation] = useMutation(SET_HOURLY_ACCOUNT_CONFIG, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const [requireLicensePlate, setRequireLicensePlate] = useState(startSessionConfig.requireLicensePlate || false);
	const [requirePhoneNumber, setRequirePhoneNumber] = useState(startSessionConfig.requirePhoneNumber || false);
	const [sessionDurationHours, setSessionDurationHours] = useState(startSessionConfig.sessionDurationHours || 0);
	const [sessionDurationMinutes, setSessionDurationMinutes] = useState(
		startSessionConfig.sessionDurationMinutes || 30,
	);
	const [defaultZoneId, setDefaultZoneId] = useState<string>(startSessionConfig.defaultZoneId || '');

	const [availableZones, setAvailableZones] = useState<{ id: string; name: string }[]>([]);
	const [availableZonesLoading, setAvailableZonesLoading] = useState(true);

	const [isSaving, setIsSaving] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const isLoading = useMemo(() => isParentLoading || isSaving, [isParentLoading, isSaving]);
	const saveDisabled =
		isLoading ||
		availableZonesLoading ||
		!defaultZoneId ||
		!availableZones.length ||
		(!sessionDurationHours && !sessionDurationMinutes);

	const lastFetchedDuration = useRef<number | null>(null);

	const [getAvailableZones] = useLazyQuery<{
		getAvailableZones: { id: string; name: string }[];
	}>(GET_AVAILABLE_ZONES, {
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only',
	});

	const fetchAvailableZones = useCallback(async () => {
		if (!(sessionDurationHours || sessionDurationMinutes) || !facilityId) {
			return;
		}

		const duration = (sessionDurationHours || 0) * 60 + (sessionDurationMinutes || 0);

		if (lastFetchedDuration.current === duration) {
			return;
		}

		setAvailableZonesLoading(true);

		try {
			const {
				data: { getAvailableZones: zones },
			} = await getAvailableZones({
				variables: {
					facilityId,
					startTime: dayjs().toISOString(),
					endTime: dayjs().add(duration, 'minute').toISOString(),
					category: HourlyCategory.S2P,
				},
			});

			setAvailableZones(zones);

			if (!defaultZoneId || !zones.some((zone) => zone.id === defaultZoneId)) {
				setDefaultZoneId(zones[0]?.id || '');
			}

			lastFetchedDuration.current = duration;
		} catch (error) {
			console.error('Error fetching available zones:', error);
			setShowSnackbar({
				message: 'Failed to fetch available zones',
				severity: 'error',
			});
		} finally {
			setAvailableZonesLoading(false);
		}
	}, [sessionDurationHours, sessionDurationMinutes, facilityId]);

	const debouncedFetchAvailableZones = useDebounce(fetchAvailableZones, 500);

	useEffect(() => {
		debouncedFetchAvailableZones();
	}, [sessionDurationHours, sessionDurationMinutes]);

	const resetState = useCallback(() => {
		setRequireLicensePlate(startSessionConfig.requireLicensePlate || false);
		setRequirePhoneNumber(startSessionConfig.requirePhoneNumber || false);
		setSessionDurationHours(startSessionConfig.sessionDurationHours || 0);
		setSessionDurationMinutes(startSessionConfig.sessionDurationMinutes || 30);
		setDefaultZoneId(startSessionConfig.defaultZoneId || null);
	}, [startSessionConfig]);

	const handleSave = useCallback(async () => {
		setIsSaving(true);

		try {
			const configInput = {
				requireLicensePlate,
				requirePhoneNumber,
				sessionDurationHours,
				sessionDurationMinutes,
				defaultZoneId,
			};

			await setHourlyAccountConfigMutation({
				variables: {
					accountId,
					feature: HourlyAccountConfigFeatureEnum.START_SESSION,
					config: JSON.stringify(configInput),
				},
			});

			setShowSnackbar({
				message: 'Successfully saved start session configuration',
				severity: 'success',
			});

			await refetchHourlyAccount();
			handleEdit(false);
		} catch (error) {
			console.error('Error saving config:', error);
			setShowSnackbar({
				message: 'Failed to save configuration',
				severity: 'error',
			});
		} finally {
			setIsSaving(false);
		}
	}, [
		requireLicensePlate,
		requirePhoneNumber,
		sessionDurationHours,
		sessionDurationMinutes,
		defaultZoneId,
		setHourlyAccountConfigMutation,
	]);

	const handleCancel = () => {
		handleEdit(false);
		resetState();
	};

	const handleEdit = (value: boolean) => {
		setIsEditing(value);
		setAccountConfigEditing(value);
	};

	useEffect(() => {
		resetState();
	}, [startSessionConfig]);

	return (
		<>
			<Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
				<Grid item>
					<Typography variant='body1' fontWeight='bold'>
						Start Session
					</Typography>
				</Grid>
				<Grid item>
					{!isEditing && (
						<Button
							size='small'
							onClick={() => {
								handleEdit(true);
							}}
							disabled={isPageEditing || isAccountConfigEditing || isLoading}
						>
							Edit
						</Button>
					)}
					{isEditing && (
						<>
							<Button size='small' variant='outlined' onClick={handleCancel} disabled={isLoading}>
								Cancel
							</Button>
							<Button size='small' onClick={handleSave} disabled={saveDisabled}>
								Save
								{isLoading && <CircularProgress sx={{ ml: 1 }} size={18} />}
							</Button>
						</>
					)}
				</Grid>
			</Grid>

			{isLoading && <CircularProgress color='inherit' />}
			{!isLoading && (
				<FormGroup>
					<FormControlLabel
						disabled={!isEditing || isLoading}
						control={
							<Switch
								checked={requireLicensePlate}
								onChange={(e) => {
									setRequireLicensePlate(e.target.checked);
								}}
							/>
						}
						label='Require License Plate'
					/>
					<FormControlLabel
						disabled={!isEditing || isLoading}
						control={
							<Switch
								checked={requirePhoneNumber}
								onChange={(e) => {
									setRequirePhoneNumber(e.target.checked);
								}}
							/>
						}
						label='Require Phone Number'
					/>
					<Grid container xs={12} spacing={2} sx={{ mt: 1 }}>
						<Grid item md={1} xs={3}>
							<FormControl fullWidth disabled={!isEditing || isLoading} required>
								<TextField
									required
									disabled={!isEditing || isLoading}
									variant='outlined'
									size='small'
									type='number'
									value={sessionDurationHours}
									onChange={(e) => setSessionDurationHours(Number(e.target.value ?? 0))}
									InputLabelProps={{ shrink: true }}
									label='Hours'
									inputProps={{ inputMode: 'numeric', step: 1, min: 0 }}
								/>
							</FormControl>
						</Grid>
						<Grid item md={1} xs={4}>
							<FormControl fullWidth disabled={!isEditing || isLoading} required>
								<TextField
									required
									disabled={!isEditing || isLoading}
									variant='outlined'
									size='small'
									type='number'
									value={sessionDurationMinutes}
									onChange={(e) => {
										const value = Number(e.target.value ?? 0);
										setSessionDurationMinutes(
											Number.isNaN(value) ? 0 : Math.min(59, Math.max(0, value)),
										);
									}}
									InputLabelProps={{ shrink: true }}
									label='Minutes'
									inputProps={{
										min: 0,
										max: 59,
										inputMode: 'numeric',
										step: 1,
									}}
								/>
							</FormControl>
						</Grid>
					</Grid>
					{availableZonesLoading ? (
						<CircularProgress color='inherit' sx={{ mt: 2 }} />
					) : (
						<Grid container xs={6} mt={2}>
							<Grid item xs={12}>
								<FormControl disabled={!isEditing || isLoading} fullWidth required>
									<InputLabel id='default-zone-label'>Default Zone</InputLabel>
									<Select
										labelId='default-zone-label'
										label='Default Zone'
										value={defaultZoneId}
										error={!availableZones?.length}
										onChange={(e) => setDefaultZoneId(e.target.value)}
									>
										<MenuItem value=''>-Select Default Zone-</MenuItem>
										{availableZones.map((zone) => (
											<MenuItem key={zone.id} value={zone.id}>
												{zone.name}
											</MenuItem>
										))}
									</Select>
									{!availableZonesLoading && !availableZones?.length && (
										<p style={{ color: 'red', marginTop: 4 }}>
											No available zones for selected duration
										</p>
									)}
								</FormControl>
							</Grid>
						</Grid>
					)}
				</FormGroup>
			)}
		</>
	);
};
